/*
 * Copyright © HatioLab Inc. All rights reserved.
 *
 * Thumbnail Scheduler
 *
 * 백엔드 헤드리스(puppeteer) thumbnail 생성을 mutation 응답 경로에서 분리하여
 * 1) save mutation latency 폭증 / cascade 장애 차단,
 * 2) 같은 board 의 빈번 저장 → puppeteer 1 회만 (per-board debounce),
 * 3) model 변경 없는 저장 → puppeteer 0 회 (hash dedup)
 * 를 달성한다.
 *
 * 클라이언트가 thumbnail 을 만들 수 없는 이유:
 *   - 권한 분리: 캡처는 인증 user 컨텍스트의 데이터 바인딩 필요.
 *   - 렌더 결정론: 모든 사용자에게 동일한 thumbnail (브라우저/디바이스/DPR 무관).
 *   - 무인 트리거: API/AI/시스템 자동 저장 시 클라이언트 없음.
 *   - 라벨 ZPL/GRF: 1-bit 정확 비트맵 — 클라이언트 anti-aliasing 비호환.
 * → 백엔드 헤드리스가 본질적이며, 비용을 *호출 횟수* 와 *응답 경로 분리* 로 줄인다.
 */

import { getDataSource, getRepository } from '@things-factory/shell'
import { thumbnail } from '../../controllers/thumbnail.js'
import { Board } from './board.js'

const EMPTY_THUMBNAIL =
  'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='

/** 처리 대기 중인 최신 model (boardId → {model, context}). 늦게 들어온 model 이 이전을 덮어쓴다. */
const pendingByBoard = new Map<string, { model: string; context: any }>()

/** 진행 중인 board — processNext 가 끝나면 자동으로 큐의 최신을 또 처리. */
const inFlightByBoard = new Set<string>()

/**
 * 직전 성공한 thumbnail 의 model hash. 같은 model 반복 저장 시 skip.
 * In-memory only — 인스턴스 재시작 시 잃지만 한 lifetime 안에서 효과 충분.
 * 영구화하려면 board 테이블에 thumbnailModelHash 컬럼 추가 필요 (별도 마이그레이션).
 */
const lastHashByBoard = new Map<string, string>()

/**
 * Internal dependencies — production 에서는 puppeteer / DB 를 사용하지만, test 에서는
 * mock 으로 교체해 hash dedup / debounce / queue 로직만 검증.
 */
export interface SchedulerDeps {
  generateThumbnail: (model: string, context: any) => Promise<string | undefined>
  saveThumbnail: (boardId: string, thumbnail: string, timestamp: Date) => Promise<void>
}

const defaultDeps: SchedulerDeps = {
  generateThumbnail: (model, context) => thumbnail({ model, context }) as Promise<string | undefined>,
  saveThumbnail: async (boardId, value, now) => {
    await getDataSource().transaction(async tx => {
      await getRepository(Board, tx).save({
        id: boardId,
        thumbnail: value,
        thumbnailUpdatedAt: now
      })
    })
  }
}

let deps: SchedulerDeps = defaultDeps

/** Test-only — DI 로 internal dependency 교체. */
export function _setSchedulerDeps(d: SchedulerDeps): void {
  deps = d
}

/** Test-only — default dependency 복원. */
export function _resetSchedulerDeps(): void {
  deps = defaultDeps
}

/** Test-only — module-level state 초기화. */
export function _resetSchedulerState(): void {
  pendingByBoard.clear()
  inFlightByBoard.clear()
  lastHashByBoard.clear()
}

/** Test-only — 현재 진행 중인 모든 작업 완료까지 대기. */
export async function _waitForIdle(): Promise<void> {
  // setImmediate 큐 비우기
  await new Promise(resolve => setImmediate(resolve))
  // 진행 중 작업 끝날 때까지 polling (테스트 환경 한정 — 보통 즉시 끝남)
  let safety = 1000
  while (inFlightByBoard.size > 0 && safety-- > 0) {
    await new Promise(resolve => setImmediate(resolve))
  }
}

/** FNV-1a 32-bit hash — 빠르고 model 변경 감지에 충분. */
export function fnv1a(s: string): string {
  let hash = 2166136261
  for (let i = 0; i < s.length; i++) {
    hash ^= s.charCodeAt(i)
    hash = Math.imul(hash, 16777619) >>> 0
  }
  return hash.toString(16)
}

/**
 * 백엔드 thumbnail 생성을 fire-and-forget 으로 schedule.
 *
 * 호출자는 await 하지 않는다 — mutation 응답이 thumbnail 완료에 차단되지 않도록.
 *
 * 동작:
 *   - 직전 성공 model 과 hash 동일 → 즉시 return (no-op).
 *   - 같은 board 에 진행 중 작업 있으면 → pending 만 갱신, 끝나면 이어서 처리.
 *   - 진행 중 작업 없음 → 즉시 시작.
 *
 * 풀 timeout / puppeteer 오류는 사용자 mutation 에 전혀 영향 없다.
 * 그 경우 lastHashByBoard 를 set 하지 않아 다음 저장에서 재시도된다.
 */
export function scheduleThumbnail(boardId: string, model: string, context: any): void {
  if (!boardId || !model) return

  const hash = fnv1a(model)
  if (lastHashByBoard.get(boardId) === hash) {
    return  // model unchanged — puppeteer 호출 불필요.
  }

  // 큐의 최신만 보존. 5 번 저장 와도 마지막 1 개만 처리됨.
  pendingByBoard.set(boardId, { model, context })

  if (inFlightByBoard.has(boardId)) return  // 진행 중 — 끝나면 큐의 최신 자동 처리.

  // setImmediate 로 mutation transaction commit 이후 시작 보장.
  setImmediate(() => {
    processNext(boardId).catch(err => {
      console.error(`[thumbnail-scheduler] board=${boardId} unexpected:`, err)
    })
  })
}

async function processNext(boardId: string): Promise<void> {
  // pending 큐에 남은 동안 계속 처리 — 진행 중에 새 model 들어와도 끝나면 이어 처리.
  while (pendingByBoard.has(boardId)) {
    const entry = pendingByBoard.get(boardId)!
    pendingByBoard.delete(boardId)
    inFlightByBoard.add(boardId)

    const hash = fnv1a(entry.model)
    try {
      const base64 = await deps.generateThumbnail(entry.model, entry.context)
      const value =
        base64 && base64 !== 'null' ? 'data:image/png;base64,' + base64 : EMPTY_THUMBNAIL

      // thumbnail 과 thumbnailUpdatedAt 을 같이 갱신 — board.updatedAt 과는 별개
      // 도메인이라 메타데이터 변경(name/description)에 영향받지 않는 thumbnail 만의
      // 시각. 클라이언트 cache-bust / push 알림 / stale 감지의 기준점.
      await deps.saveThumbnail(boardId, value, new Date())
      lastHashByBoard.set(boardId, hash)
    } catch (e) {
      // 풀 timeout / puppeteer 오류 — 다음 저장에서 재시도되도록 hash set 안 함.
      // 기존 board.thumbnail 은 그대로 유지 (DB save 시 thumbnail field 미터치).
      console.warn(`[thumbnail-scheduler] board=${boardId} failed: ${(e as Error).message}`)
    } finally {
      inFlightByBoard.delete(boardId)
    }
  }
}

/**
 * 진단 / 운영용 — 현재 큐 / 진행 상태.
 * 추후 admin endpoint 에서 활용 가능.
 */
export function getSchedulerStats() {
  return {
    pending: pendingByBoard.size,
    inFlight: inFlightByBoard.size,
    cachedHashes: lastHashByBoard.size
  }
}
